import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical, Eye, Edit, Trash2, UserPlus, Ban, RotateCcw } from 'lucide-react';

export default function ActionMenu({ onView, onEdit, onAssign, onReactivate, onDelete, deleteLabel = 'Delete' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const updatePosition = () => {
      if (buttonRef.current && isOpen) {
        const rect = buttonRef.current.getBoundingClientRect();
        const menuHeight =
          48 +
          (onView ? 40 : 0) +
          (onAssign ? 40 : 0) +
          (onReactivate ? 40 : 0) +
          (onEdit ? 40 : 0) +
          (onDelete ? 40 : 0);
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        
        // Calculate position
        let top = rect.bottom + 4; // Default: show below
        let right = window.innerWidth - rect.right;
        
        // If not enough space below and more space above, show above
        if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
          top = rect.top - menuHeight - 4;
        }
        
        setMenuPosition({ top, right });
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, onAssign, onReactivate, onDelete, onView, onEdit]);

  return (
    <>
      <div className="relative inline-block">
        <button
          ref={buttonRef}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          className="p-2 rounded-lg hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark transition-colors"
        >
          <MoreVertical className="w-4 h-4 text-text-secondary dark:text-text-secondary-dark" />
        </button>
      </div>

      {isOpen && createPortal(
        <div
          ref={menuRef}
          className="fixed w-48 bg-surface dark:bg-surface-dark border border-border dark:border-border-dark rounded-lg shadow-2xl z-[9999]"
          style={{
            top: `${menuPosition.top}px`,
            right: `${menuPosition.right}px`,
          }}
        >
          <div className="py-1">
            {onView && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onView();
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-text dark:text-text-dark hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark transition-colors"
              >
                <Eye className="w-4 h-4" />
                View
              </button>
            )}
            {onAssign && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAssign();
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-text dark:text-text-dark hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark transition-colors"
              >
                <UserPlus className="w-4 h-4" />
                Assign driver
              </button>
            )}
            {onReactivate && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onReactivate();
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-text dark:text-text-dark hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Reactivate
              </button>
            )}
            {onEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-text dark:text-text-dark hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark transition-colors"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
            )}
            {onDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-danger hover:bg-danger-light dark:hover:bg-danger-dark transition-colors"
              >
                {deleteLabel === 'Deactivate' ? <Ban className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
                {deleteLabel}
              </button>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
